import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "@/config/firebase";

/**
 * Request notification permission and get FCM token for Web Push (chat notifications).
 * Call after login and send the token to your backend to store per user/device.
 * @param vapidKey - VAPID public key from Firebase Console → Cloud Messaging → Web configuration → Key pair
 * @returns FCM token or null if permission denied / messaging unsupported
 */
export async function registerPushToken(vapidKey: string): Promise<string | null> {
  if (!messaging || !vapidKey?.trim()) return null;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return null;

    const token = await getToken(messaging, {
      vapidKey: vapidKey.trim(),
      serviceWorkerRegistration: await getServiceWorkerRegistration(),
    });
    return token || null;
  } catch (e) {
    console.warn("[Push] Failed to get FCM token:", e);
    return null;
  }
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!("serviceWorker" in navigator)) return undefined;
  try {
    // Register (or get existing) so FCM uses this SW for background push
    const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js", { scope: "/" });
    return reg;
  } catch {
    return undefined;
  }
}

/**
 * Register a handler for foreground FCM messages (app open). Use for in-app toasts or UI updates.
 * Per FCM doc: when the app is in the foreground, only onMessage fires; background uses the service worker.
 */
export function registerForegroundMessageHandler(callback: (payload: import("firebase/messaging").MessagePayload) => void): void {
  if (!messaging) return;
  onMessage(messaging, callback);
}

/** FCM data payload for chat (see docs/FCM_WEB_PUSH_FRONTEND_INTEGRATION_PARAMS.md §3.3). Backend may send link and/or context IDs. */
export interface FcmChatData {
  type?: string;
  conversationId?: string;
  link?: string;
  tournamentId?: string;
  /** For booking_chat: open table booking at this branch (center API sends these). */
  organizerId?: string;
  branchId?: string;
  tableNumber?: string;
  /** Booking UUID (same as conversationId for booking_chat). */
  bookingId?: string;
  /** Customer profile ID (when organizer receives push). In customer portal used for consistency with backend payload. */
  customerId?: string;
}

/**
 * Normalize backend link to app path (pathname + search). Handles full URL or path.
 * Backend sends data.link when CUSTOMER_PORTAL_BASE_URL is set (see FCM doc §3.1).
 */
function normalizeLinkToPath(link: string): string {
  const s = link.trim();
  if (!s) return "/";
  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      const u = new URL(s);
      return u.pathname + u.search;
    } catch {
      return s.startsWith("/") ? s : "/" + s;
    }
  }
  return s.startsWith("/") ? s : "/" + s.replace(/^\//, "");
}

/**
 * Merge query string from link into base path (keeps bookingId, branchId, customerId, organizerId, tournamentId on opened page).
 */
function mergeSearchParams(basePathWithQuery: string, search: string): string {
  if (!search || !search.startsWith("?")) return basePathWithQuery;
  const base = basePathWithQuery.split("?")[0];
  const baseQuery = basePathWithQuery.includes("?") ? basePathWithQuery.slice(basePathWithQuery.indexOf("?") + 1) : "";
  const linkParams = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const combined = new URLSearchParams(baseQuery);
  for (const [k, v] of linkParams) if (v) combined.set(k, v);
  const q = combined.toString();
  return q ? base + "?" + q : base;
}

/**
 * Map backend path convention to customer portal routes (doc §3.1).
 * Preserves query params from data.link (e.g. bookingId, branchId, organizerId, tournamentId, customerId) so the opened page can read them.
 */
function mapBackendPathToCustomerPath(pathname: string, search: string): string {
  const path = pathname.replace(/\/+$/, "") || "/";
  let mapped: string;
  if (path.startsWith("/chat/booking/")) {
    const id = path.slice("/chat/booking/".length).split("/")[0];
    mapped = id ? "/customer/bookings?openChat=" + encodeURIComponent(id) : "/customer/bookings";
  } else if (path.startsWith("/chat/tournament/")) {
    const id = path.slice("/chat/tournament/".length).split("/")[0];
    mapped = id ? "/tournament/" + encodeURIComponent(id) + "?openChat=1" : "/";
  } else if (path.startsWith("/chat/dm/")) {
    const id = decodeURIComponent(path.slice("/chat/dm/".length).split("/")[0] || "");
    mapped = id ? "/customer/friends?conversation=" + encodeURIComponent(id) : "/customer/friends";
  } else if (path.startsWith("/chat/group/")) {
    const id = path.slice("/chat/group/".length).split("/")[0];
    mapped = id ? "/customer/friends?conversation=" + encodeURIComponent(id) : "/customer/friends";
  } else {
    return path + (search ? (search.startsWith("?") ? search : "?" + search) : "");
  }
  return mergeSearchParams(mapped, search);
}

/**
 * Build app path from FCM chat payload (notification click or foreground navigate).
 * Prefers data.link (clickable deep link from backend); else uses type + IDs so the right page and chat open.
 */
export function getChatPathFromFcmData(data: FcmChatData | null | undefined): string {
  if (!data) return "/";

  const link = data.link;
  if (link && typeof link === "string" && link.trim() !== "") {
    const pathAndSearch = normalizeLinkToPath(link);
    const pathname = pathAndSearch.split("?")[0] || "/";
    const search = pathAndSearch.includes("?") ? "?" + pathAndSearch.split("?").slice(1).join("?") : "";
    if (pathname.startsWith("/chat/")) {
      return mapBackendPathToCustomerPath(pathname, search);
    }
    return pathAndSearch.startsWith("/") ? pathAndSearch : "/" + pathAndSearch;
  }

  const type = data.type;
  const conversationId = data.conversationId || data.bookingId;

  if (type === "tournament_chat" && data.tournamentId) {
    const base = "/tournament/" + encodeURIComponent(data.tournamentId);
    const q = new URLSearchParams();
    if (conversationId) q.set("openChat", conversationId);
    else q.set("openChat", "1");
    if (data.branchId) q.set("branchId", data.branchId);
    return base + "?" + q.toString();
  }

  if (type === "booking_chat" && conversationId) {
    const organizerId = data.organizerId;
    const branchId = data.branchId;
    if (organizerId && branchId) {
      return "/customer/organizers/" + encodeURIComponent(organizerId) + "/branches/" + encodeURIComponent(branchId) + "/tables?openChat=" + encodeURIComponent(conversationId);
    }
    const q = new URLSearchParams({ openChat: conversationId });
    if (organizerId) q.set("organizerId", organizerId);
    if (branchId) q.set("branchId", branchId);
    return "/customer/bookings?" + q.toString();
  }

  if (type === "direct_message" || type === "group_chat") {
    return conversationId ? "/customer/friends?conversation=" + encodeURIComponent(conversationId) : "/customer/friends";
  }

  return "/";
}
